import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import TicketsList from './modules/TicketsList';
import { listSupportTickets } from '../../../services/supportApi';
import { resolveSupportError } from './supportUtils';
import './SupportSection.css';

export default function SupportTicketsSection({ user, empresaId, empresaOwnerId }) {
  const [tickets, setTickets] = useState([]);
  const [ticketsLoading, setTicketsLoading] = useState(false);
  const [ticketsError, setTicketsError] = useState('');

  const userId = user?.id || '';
  const companyId = empresaId || '';
  const companyOwnerId = empresaOwnerId || '';
  const isAdmin = (user?.role || '').toLowerCase() === 'admin';
  const includeCompanyTickets =
    Boolean(companyId) &&
    (isAdmin || (user?.company && user.company === companyId) || userId === companyOwnerId);

  const loadTickets = useCallback(async () => {
    if (!userId) {
      setTickets([]);
      return;
    }

    setTicketsLoading(true);
    setTicketsError('');

    try {
      const { items } = await listSupportTickets({
        reporterId: userId,
        companyId: includeCompanyTickets ? companyId : undefined,
        includeCompanyTickets,
        perPage: 200,
        sort: '-created',
      });
      const uniqueById = new Map(items.map(item => [item.id, item]));
      setTickets(Array.from(uniqueById.values()));
    } catch (error) {
      setTicketsError(resolveSupportError(error));
    } finally {
      setTicketsLoading(false);
    }
  }, [userId, companyId, includeCompanyTickets]);

  useEffect(() => {
    loadTickets();
  }, [loadTickets]);

  const ticketsStats = useMemo(() => {
    if (!tickets.length) {
      return {
        open: 0,
        resolved: 0,
        avgResolution: '—',
      };
    }
    const openCount = tickets.filter(ticket => ticket.status === 'open').length;
    const resolvedTickets = tickets.filter(ticket => ticket.status === 'resolved' && ticket.resolvedAt);
    const resolvedCount = resolvedTickets.length;
    const averageResolutionHours = resolvedTickets.length
      ? (
          resolvedTickets.reduce((acc, ticket) => {
            const createdAt = ticket.created ? new Date(ticket.created).getTime() : 0;
            const resolvedAt = new Date(ticket.resolvedAt).getTime();
            if (!createdAt || !resolvedAt || Number.isNaN(resolvedAt) || Number.isNaN(createdAt)) {
              return acc;
            }
            const diffHours = Math.max(1, (resolvedAt - createdAt) / (1000 * 60 * 60));
            return acc + diffHours;
          }, 0) / resolvedTickets.length
        ).toFixed(1)
      : null;

    return {
      open: openCount,
      resolved: resolvedCount,
      avgResolution: averageResolutionHours ? `${averageResolutionHours} h` : '—',
    };
  }, [tickets]);

  const stats = [
    { label: 'Abiertos', value: ticketsStats.open, tone: 'blue', icon: '●' },
    { label: 'Resueltos', value: ticketsStats.resolved, tone: 'slate', icon: '✓' },
    { label: 'Tiempo promedio', value: ticketsStats.avgResolution, tone: 'violet', icon: '⏱' },
  ];

  return (
    <section className="support-shell support-shell--wide">
      <header className="support-header">
        <div className="support-header__row">
          <div>
            <p className="support-eyebrow">Mesa de ayuda</p>
            <h1>Mis tickets</h1>
            <p className="support-header__subtitle">
              Controla tus conversaciones de soporte con un resumen claro de estados y tiempos de respuesta.
            </p>
          </div>
          <div className="support-header__actions">
            <button type="button" className="support-btn support-btn--ghost" onClick={loadTickets}>
              Actualizar
            </button>
            <Link to="/dashboard?section=support-new" className="support-btn support-btn--primary">
              Crear ticket
            </Link>
          </div>
        </div>
        <div className="support-stats">
              {stats.map(stat => (
                <article key={stat.label} className="support-stat-card">
                  <span
                    className={`support-stat-icon support-stat-icon--${stat.tone}`}
                    aria-hidden="true"
                  >
                    {stat.icon}
                  </span>
                  <div>
                    <p>{stat.label}</p>
                    <strong>{stat.value}</strong>
                  </div>
                </article>
          ))}
        </div>
      </header>

      <TicketsList tickets={tickets} loading={ticketsLoading} error={ticketsError} onRefresh={loadTickets} />
    </section>
  );
}
